/**
 * Drives the page a pilot advisor is held on until they accept their program's participation
 * agreement (see `computeAdvisorForceRedirect`).
 *
 * Accepting saves the acceptance, then reloads the session so the server recomputes where the
 * advisor goes next. Navigation waits until the reloaded session no longer points at this page;
 * leaving earlier would let the router send the advisor straight back here. If the reload settles
 * still pointing here (it failed), waiting stops so the advisor can try again instead of being
 * left on "Saving...".
 */
import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getPilotParticipationAgreement, PARTICIPATION_AGREEMENT_REDIRECT_PATH } from '@shared';

import { participationAgreementApi } from '@client/api/waterlily';
import { fetchSessionForAuthRequest } from '@client/model/sessionModel';
import { selectFeatureFlags, selectSession } from '@client/model/selectors';

export function useParticipationAgreement(advisorId: string) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const session = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const agreement = getPilotParticipationAgreement(featureFlags);
  const [hasChecked, setHasChecked] = useState(false);
  const [isWaitingForSession, setIsWaitingForSession] = useState(false);

  const acceptMutation = useMutation({
    mutationFn: () => participationAgreementApi.accept(advisorId),
    onSuccess: () => {
      dispatch(fetchSessionForAuthRequest({ invalidating: true }));
      setIsWaitingForSession(true);
    },
    onError: error => {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error('Failed to save your agreement', {
        description: `${message}. Please try again.`,
      });
    },
  });

  useEffect(() => {
    if (!isWaitingForSession || session.isInvalidating) {
      return;
    }

    setIsWaitingForSession(false);
    if (session.forceRedirect !== PARTICIPATION_AGREEMENT_REDIRECT_PATH) {
      navigate('/');
      return;
    }

    toast.error('We could not continue', {
      description: 'Your agreement was saved, but loading your account failed. Please try again.',
    });
  }, [isWaitingForSession, session.isInvalidating, session.forceRedirect, navigate]);

  const accept = useCallback(() => {
    if (hasChecked) {
      acceptMutation.mutate();
    }
  }, [hasChecked, acceptMutation]);

  return {
    agreement,
    hasChecked,
    setHasChecked,
    accept,
    isSubmitting: acceptMutation.isPending || isWaitingForSession,
  };
}
